import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { CustomerCardService } from '../../services/customer-card.service';
import { AuthService } from '../../services/auth.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SettingsService } from '../../services/settings.service';

@Component({
  selector: 'app-add-card',
  standalone: true,
  imports: [CommonModule, RouterModule, ToastModule, ProgressSpinnerModule],
  providers: [MessageService],
  templateUrl: './add-card.html',
  styleUrl: './add-card.css'
})
export class AddCardComponent implements OnInit {
  isLoading = signal(false);
  platformId = signal<string | null>(null);
  platformName = signal<string | null>(null);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cardService: CustomerCardService,
    private authService: AuthService,
    private messageService: MessageService,
    private settingsService: SettingsService
  ) {}

  ngOnInit(): void {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/signin']);
      return;
    }

    // Get platformId from route params or query params
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.platformId.set(params['id']);
      }
    });

    this.route.queryParams.subscribe(params => {
      if (params['platformId']) {
        this.platformId.set(params['platformId']);
      }
      
      // Try to get platformId from localStorage as fallback
      if (!this.platformId()) {
        const storedPlatformId = localStorage.getItem('arkifihub_platformId');
        if (storedPlatformId) {
          this.platformId.set(storedPlatformId);
        }
      }
      
      // Get platform name from localStorage (stored when navigating from store)
      const storedPlatformName = localStorage.getItem('arkifihub_platformName');
      if (storedPlatformName) {
        this.platformName.set(storedPlatformName);
      }
    });
  }

  async addCard(): Promise<void> {
    const platformId = this.platformId();
    if (!platformId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Platform ID is required',
        life: 5000
      });
      return;
    }

    this.isLoading.set(true);

    try {
      // Get environment from SettingsService
      const environment = this.settingsService.getEnvironment();
      
      // Build callback URL using current origin (without environment in URL)
      const baseUrl = window.location.origin;
      const callbackUrl = `${baseUrl}/add-card-callback?platformId=${platformId}`;

      const result = await this.cardService.initializeAddCardPayment(
        platformId,
        environment,
        callbackUrl
      );

      if (result.success && result.data?.authorizationUrl) {
        // Redirect to Paystack
        window.location.href = result.data.authorizationUrl;
      } else {
        this.isLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: result.message || 'Failed to initialize payment',
          life: 5000
        });
      }
    } catch (error: any) {
      this.isLoading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'An error occurred',
        life: 5000
      });
      console.error('Error adding card:', error);
    }
  }

  goBack(): void {
    const platformId = this.platformId();
    const platformName = this.platformName();
    
    // Navigate back to store page if we have both platformId and platformName
    if (platformId && platformName) {
      const encodedName = encodeURIComponent(platformName);
      this.router.navigate(['/store', platformId, encodedName]);
    } else {
      // Fallback to marketplace if we don't have store info
      this.router.navigate(['/marketplace']);
    }
  }
}
